import {useEffect, useState} from 'react';
import moment from 'moment';
import {DayPickerRangeController} from 'react-dates';
import {FilterSelection} from "../../../types/filterSelection";

moment.locale('nl');

const isDayInBookableRange = (day, highlightedDates, selectedNights) => {
    if (isDayHighlighted(day, highlightedDates)) {
        return true;
    }

    const arrivalDate = day.clone().subtract(selectedNights, 'day');
    if (isDayHighlighted(arrivalDate, highlightedDates)) {
        return true;
    }

    // Amount of previous dates to check
    const previousDayCount = Array.from(Array(selectedNights + 1).keys());
    // previousDayCount.shift();
    const arrivalDates = previousDayCount.map(nights => {
        const dateString = day.clone().subtract(nights, 'day').format("YYYY-MM-DD");
        const date = highlightedDates.find((date) => {
            return date === dateString
        });

        if (date) {
            return true;
        }

        return false;
    });

    return arrivalDates.filter(isAvailable => isAvailable === true).length === 0;
};

const isDayHighlighted = (day, highlightedDates) => {
    const dateString = day.format("YYYY-MM-DD");

    const date = highlightedDates.find((date) => {
        return date === dateString;
    });

    if (date) {
        return true;
    }

    return false;
};

const isDayAvailable = (day, highlightedDates, selectedNights) => {
    const arrivalDate = day.clone().subtract(selectedNights, 'day');

    const dateString = day.format("YYYY-MM-DD");
    const arrivalDateString = arrivalDate.format("YYYY-MM-DD");

    const date = highlightedDates.find((date) => {
        return date === dateString || date === arrivalDateString;
    });

    if (date) {
        return true;
    }

    return false;
};

const getArrivalDates = (availability, nights) => {
    let today = moment().format("YYYY-MM-DD");
    return availability.availableDates
        .filter(date => {
            let found = date.availableNights.find(availableNight => parseInt(availableNight.nights) === parseInt(nights));

            return found !== undefined;
        })
        .map((date) => {
            return date.date;
        })
        .filter((date) => date >= today);
};

interface DateRangePickerProps {
    searchSelection: FilterSelection,
    updateSearchSelection: (searchSelection: FilterSelection) => void,
    activateOffset: boolean,
    orientation: string
    daySize: number,
    numberOfMonths: number,
    availability: any,
    selectedNights: number
}

export default function DateRangePicker(
    {
        searchSelection,
        updateSearchSelection,
        activateOffset,
        orientation,
        daySize,
        numberOfMonths,
        availability,
        selectedNights = 7
    }: DateRangePickerProps) {
    const [startDate, setStartDate] = useState(searchSelection.startDate ? moment(searchSelection.startDate) : null);
    const [endDate, setEndDate] = useState(searchSelection.endDate ? moment(searchSelection.endDate) : null);
    const [focusedInput, setFocusedInput] = useState('startDate');
    const [highlightedDates, setHighlightedDates] = useState([]);

    useEffect(() => {
        if (!startDate) {
            return;
        }

        const selectedDate = document.getElementsByClassName('CalendarDay__selected');
        if (!selectedDate[0]) {
            return;
        }

        if (selectedDate[0] instanceof HTMLElement) {
            const offset = selectedDate[0].parentElement.parentElement.parentElement.parentElement.parentElement.getBoundingClientRect();
            const top = offset.top - 100;
            const modal = document.getElementsByClassName('modal-body');
            if (!modal[0]) {
                return;
            }
            setTimeout(
                () => modal[0].scrollTo({top: top, behavior: 'smooth'}),
                500
            );
        }
    }, []);

    useEffect(() => {
        const newStartDate = searchSelection.startDate ? moment(searchSelection.startDate) : null;
        const newEndDate = searchSelection.endDate ? moment(searchSelection.endDate) : null;
        setStartDate(newStartDate);
        setEndDate(newEndDate);
    }, [searchSelection]);

    useEffect(() => {
        if (!availability) {
            return;
        }
        setHighlightedDates(getArrivalDates(availability, selectedNights));
    }, [availability, selectedNights]);

    const onDatesChange = ({startDate, endDate}) => {
        setStartDate(startDate);
        setEndDate(endDate);
        searchSelection.startDate = startDate.format('YYYY-MM-DD');
        searchSelection.endDate = undefined;

        if (selectedNights) {
            searchSelection.endDate = endDate.format('YYYY-MM-DD');
        }

        if (focusedInput === 'endDate' && endDate) {
            searchSelection.endDate = endDate.format('YYYY-MM-DD');
        }

        updateSearchSelection(searchSelection);
    }

    const onFocusChange = (focusedInput) => {
        setFocusedInput(!focusedInput ? 'startDate' : focusedInput);
    }

    return (
        <DayPickerRangeController
            navPrev={<div/>}
            initialVisibleMonth={() => moment()}
            // isDayBlocked={(day) => !isArrivalDayAvailable(day, highlightedDates)}
            isDayBlocked={(day) => !isDayInBookableRange(day, highlightedDates, selectedNights)}
            isOutsideRange={(day) => isDayInBookableRange(day, highlightedDates, selectedNights) && !isDayAvailable(day, highlightedDates, selectedNights)}
            // enableOutsideDays
            // isOutsideRange={day => !isInclusivelyAfterDay(day, moment())}
            isDayHighlighted={(day) => isDayHighlighted(day, highlightedDates)}
            startDate={startDate} // momentPropTypes.momentObj or null,
            endDate={endDate} // momentPropTypes.momentObj or null,
            endDateOffset={day => day.add(selectedNights, 'days')}
            onDatesChange={(dates) => {
                onDatesChange(dates);
            }} // PropTypes.func.isRequired,
            focusedInput={focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
            onFocusChange={focusedInput => onFocusChange(
                focusedInput)
            }
            noBorder
            daySize={daySize ? daySize : 50}
            numberOfMonths={numberOfMonths ? numberOfMonths : 2}
            verticalHeight={orientation === 'vertical' ? 4000 : undefined}
            orientation={orientation ? orientation : undefined}
        />
        // <DayPickerRangeController
        //     // isOutsideRange={(day) => !isArrivalDayAvailable(day, highlightedDates)}
        //     isOutsideRange={day => !isInclusivelyAfterDay(day, moment())}
        //     // isDayHighlighted={(day) => isArrivalDayAvailable(day, highlightedDates)}
        //     orientation={orientation ? orientation : undefined}
        //     // startDateOffset={day => day}
        //     // endDateOffset={day => day.add(selectedNights, 'days')}
        //     // endDateOffset={day => day.add(7, 'days')}
        //     minimumNights={selectedNights}
        //     enableOutsideDays={false}
        //     hideKeyboardShortcutsPanel
        //     noBorder
        //     daySize={daySize ? daySize : 50}
        //     numberOfMonths={numberOfMonths ? numberOfMonths : 2}
        //     startDate={startDate} // momentPropTypes.momentObj or null,
        //     endDate={endDate} // momentPropTypes.momentObj or null,
        //     verticalHeight={orientation === 'vertical' ? 4000 : undefined}
        //     onDatesChange={(dates) => {
        //         onDatesChange(dates);
        //     }} // PropTypes.func.isRequired,
        //     focusedInput={focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
        //     onFocusChange={focusedInput => onFocusChange(
        //         focusedInput)} // PropTypes.func.isRequired,
        // />
    )
}
